import React, { useState, useEffect } from 'react';
import { Brain, Sparkles, Clock, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import AIService, { AISummary } from '../../services/AIService';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import { Message } from '../../types/messages';
import toast from 'react-hot-toast';

interface MessageSummaryProps {
  messages: Message[];
  timeframe?: string;
  className?: string;
}

interface ExtendedMessageSummaryProps extends MessageSummaryProps {
  onSummaryChanged?: (hasSummary: boolean) => void;
}

export function MessageSummary({ messages, timeframe = 'today', className = '', onSummaryChanged }: ExtendedMessageSummaryProps) {
  const { user } = useAuth();
  const { selectedSchool } = useSchool();
  const school = selectedSchool;
  const [summary, setSummary] = useState<AISummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(true);

  // Automatically generate summary when component mounts or messages change
  useEffect(() => {
    console.log('[MessageSummary] Effect triggered:', {
      messageCount: messages.length,
      hasSummary: !!summary,
      loading,
      hasApiKey
    });
    
    if (messages.length >= 5 && !summary && !loading) {
      console.log('[MessageSummary] Triggering auto-generate summary');
      generateSummary();
    }
  }, [messages.length]); // Only depend on message count

  const generateSummary = async () => {
    console.log('[MessageSummary] generateSummary called', {
      user: !!user,
      school: !!school,
      messageCount: messages.length
    });
    
    if (!user || !school || messages.length === 0) {
      console.log('[MessageSummary] Skipping generation - missing requirements');
      return;
    }

    try {
      setLoading(true);
      console.log('[MessageSummary] Calling AIService.summarizeMessages...');
      
      const newSummary = await AIService.summarizeMessages(
        messages, 
        school.id, 
        user.uid, 
        timeframe
      );
      
      console.log('[MessageSummary] Summary received:', newSummary);
      setSummary(newSummary);
      setHasApiKey(true);
      
      // Notify parent that summary has loaded
      onSummaryChanged?.(true);
    } catch (error: any) {
      console.error('[MessageSummary] Error generating summary:', error);
      // Check if it's an API key error
      if (error.message?.includes('API key not configured')) {
        console.log('[MessageSummary] API key not configured');
        setHasApiKey(false);
        // Don't show error toast for missing API key
      } else {
        toast.error('Failed to generate AI summary');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return '😊';
      case 'negative': return '😟';
      default: return '😐';
    }
  };

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-50';
      case 'negative': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (messages.length < 3) {
    console.log('[MessageSummary] Not enough messages to show summary:', messages.length);
    return null;
  }
  
  console.log('[MessageSummary] Rendering component:', {
    messageCount: messages.length,
    hasSummary: !!summary,
    loading,
    hasApiKey
  });

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-blue-600" />
          <h3 className="text-sm font-medium text-gray-900">Conversation Summary</h3>
          {summary && (
            <span className={`px-2 py-1 text-xs rounded-full ${getSentimentColor(summary.sentiment)}`}>
              {getSentimentIcon(summary.sentiment)} {summary.sentiment}
            </span>
          )}
        </div>
        
        {/* Only show refresh button when we have a summary */}
        {summary && !loading && (
          <button
            onClick={generateSummary}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh summary"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-blue-600 animate-pulse" />
            <p className="text-sm text-gray-500">Analyzing conversation...</p>
          </div>
        </div>
      )}

      {/* Summary Content */}
      {summary && !loading && (
        <div className="px-4 pb-4 space-y-3">
          {/* Quick Summary */}
          <div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {summary.summary}
            </p>
            {summary.priority && summary.priority !== 'low' && (
              <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded ${getPriorityColor(summary.priority)}`}>
                {summary.priority} priority
              </span>
            )}
          </div>

          {/* Key Points - Always visible when we have them */}
          {summary.keyPoints && summary.keyPoints.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Key Points
              </h4>
              <ul className="space-y-1">
                {summary.keyPoints.slice(0, 3).map((point, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="line-clamp-2">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Items - Show if any exist */}
          {summary.actionItems && summary.actionItems.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 text-orange-600" />
                Action Required
              </h4>
              <ul className="space-y-1">
                {summary.actionItems.slice(0, 2).map((item, index) => (
                  <li key={index} className="text-sm text-orange-700 flex items-start gap-2">
                    <span className="w-1 h-1 bg-orange-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="line-clamp-2">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      )}

      {/* No Summary State - Only show if API key is missing */}
      {!summary && !loading && !hasApiKey && (
        <div className="p-4">
          <p className="text-sm text-gray-500 text-center">
            AI summaries are not configured
          </p>
        </div>
      )}
    </div>
  );
}

export default MessageSummary;